import { Typography } from '@mui/material'
import {
  ErrorText,
  WarningCircleIcon,
  WarningContainer,
  WarningText,
} from 'components/Message'
import { IdVerificationStatus, Routes } from 'constants/index'
import { gevent } from 'helpers/gtag'
import { observer } from 'mobx-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { AuthStore, NavStore } from 'stores'
import { useInstance } from 'util/di'
import Step from './Step'

import { useViewport } from 'helpers/hooks'
import * as S from './styled'
import VerificationBlockedModal from './VerificationBlockedModal'

/**
 * Interpolated translations
 *
 * i18n-tasks-use t('enrollment.onboarding.popup.title')
 * i18n-tasks-use t('enrollment.onboarding.popup.complete')
 * i18n-tasks-use t('enrollment.onboarding.popup.status.awaiting_id')
 * i18n-tasks-use t('enrollment.onboarding.popup.status.awaiting_upload')
 * i18n-tasks-use t('enrollment.onboarding.popup.status.pending')
 * i18n-tasks-use t('enrollment.onboarding.popup.status.title')
 * i18n-tasks-use t('enrollment.onboarding.popup.steps.review.info')
 * i18n-tasks-use t('enrollment.onboarding.popup.steps.review.title')
 * i18n-tasks-use t('enrollment.onboarding.popup.steps.upload.info')
 * i18n-tasks-use t('enrollment.onboarding.popup.steps.upload.title')
 * i18n-tasks-use t('enrollment.onboarding.popup.steps.verify.info')
 * i18n-tasks-use t('enrollment.onboarding.popup.steps.verify.title')
 * i18n-tasks-use t('enrollment.onboarding.popup.steps.verify.action')
 *
 */
const OnboardingPopup = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { isMobile } = useViewport()

  const { submittedCount } = useInstance<NavStore>('NavStore')
  const { session } = useInstance<AuthStore>('AuthStore')

  const [open, setOpen] = useState(false)
  const [verificationModalVisibility, setVerificationModalVisibility] =
    useState(false)

  const toggle = () => setOpen(o => !o)

  const status = session?.id_inquiry_status as IdVerificationStatus

  const statusFailed = useMemo(
    () =>
      [IdVerificationStatus.Failed, IdVerificationStatus.Declined].includes(
        status
      ) || session?.hasVerificationBlocked,
    [status, session]
  )
  const idVerificationStart =
    !status ||
    [
      IdVerificationStatus.NotStarted,
      IdVerificationStatus.Started,
      IdVerificationStatus.Expired,
    ].includes(status)
  let cta = t('persona.toast.verify')
  let href = ''

  const renderStatus = useMemo(() => {
    if (submittedCount < 25) {
      return t('enrollment.onboarding.popup.status.awaiting_upload')
    } else if (submittedCount >= 25 && !session?.isVerified) {
      return t('enrollment.onboarding.popup.status.awaiting_id')
    }
    return t('enrollment.onboarding.popup.status.pending')
  }, [session?.isVerified, submittedCount, t])

  const renderUploadState = useMemo(() => {
    if (submittedCount < 25) {
      return 'awaiting'
    } else if (submittedCount >= 25) {
      return 'complete'
    }
    return 'inactive'
  }, [submittedCount])

  const renderVerifyState = useMemo(() => {
    if (submittedCount >= 25 && !session?.isVerified) {
      return 'awaiting'
    } else if (submittedCount >= 25 && session?.isVerified) {
      return 'complete'
    }
    return 'inactive'
  }, [session?.isVerified, submittedCount])

  const renderReviewState = useMemo(() => {
    if (renderVerifyState === 'complete') {
      return 'awaiting'
    }
    return 'inactive'
  }, [renderVerifyState])

  if (idVerificationStart && !statusFailed) {
    cta = t('persona.toast.verify')
    href = session ? session.entityVerificationUrl : ''
  } else if (renderUploadState === 'complete' && statusFailed) {
    cta = t('persona.toast.contact')
  }

  const navigateToUpload = () => {
    navigate(Routes.PortfolioUpload)
    toggle()
  }

  const verifyId = () => {
    if (statusFailed) {
      window.open(
        'https://eezycontributors.zendesk.com/hc/en-us/requests/new',
        '_blank',
        'noopener,noreferrer'
      )
    } else {
      if (idVerificationStart) {
        gevent('verify_id_start', 'ID')
      }

      if (href) {
        window.open(href, '_blank', 'noopener,noreferrer')
      }

      toggle()
    }
  }

  return (
    <S.StickyContainer open={open} className="fade-in" $isMobile={isMobile}>
      <S.Header onClick={toggle}>
        <S.TitleWrapper>
          <Typography variant="h6">
            {t('enrollment.onboarding.popup.title')}
          </Typography>
          {open ? (
            <S.IconAction>
              <S.ArrowUDown />
            </S.IconAction>
          ) : (
            <S.IconAction>
              <S.ArrowUp />
            </S.IconAction>
          )}
        </S.TitleWrapper>
        <S.StatusWrapper>
          <WarningContainer>
            <WarningCircleIcon />
            <WarningText>{`${t(
              'enrollment.onboarding.popup.status.title'
            )}: ${renderStatus}`}</WarningText>
          </WarningContainer>
        </S.StatusWrapper>
      </S.Header>
      <S.Content>
        {open && (
          <>
            <S.Section>
              <Typography variant="body1">
                {t('enrollment.onboarding.popup.complete')}
              </Typography>
            </S.Section>
            <S.Separator />
            <S.Section>
              <Step
                title={t('enrollment.onboarding.popup.steps.upload.title')}
                content={t('enrollment.onboarding.popup.steps.upload.info')}
                step={1}
                type="upload"
                state={renderUploadState}
                buttonAction={navigateToUpload}
                buttonText={t('util.upload')}
              />
            </S.Section>
            <S.Separator />
            <S.Section>
              <Step
                title={t('enrollment.onboarding.popup.steps.verify.title')}
                content={t('enrollment.onboarding.popup.steps.verify.info')}
                step={2}
                type="verify"
                state={renderVerifyState}
                buttonAction={verifyId}
                buttonText={cta}
                disableAction={renderVerifyState === 'complete'}
              />
              {renderUploadState === 'complete' && statusFailed ? (
                <S.ErrorWrapper>
                  <ErrorText>
                    {t('enrollment.onboarding.popup.steps.verify.error')}
                  </ErrorText>
                </S.ErrorWrapper>
              ) : null}
            </S.Section>
            <S.Separator />
            <S.Section>
              <Step
                title={t('enrollment.onboarding.popup.steps.review.title')}
                content={t('enrollment.onboarding.popup.steps.review.info')}
                step={3}
                type="review"
                state={renderReviewState}
              />
            </S.Section>
          </>
        )}
      </S.Content>
      {verificationModalVisibility ? (
        <VerificationBlockedModal
          modalOpen={verificationModalVisibility}
          setModalOpen={setVerificationModalVisibility}
        />
      ) : null}
    </S.StickyContainer>
  )
}

export default observer(OnboardingPopup)
